import React, { useState, useEffect } from 'react';

import { Checkbox } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import PostField from 'components/feed/PostField';
import { EditorState } from 'draft-js';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import styled, { css } from 'styled-components';
import { StyledDrawer } from 'styles/admin';
import { StyledDivider, StyledTypography, NewStyledIconButton } from 'styles/common';

import { SEARCH_WIDGET_TYPES } from 'constants/ProjectConstants';
import { StyledIcon } from 'constants/icons';
import { sendShareExperiencePublishedEvent } from 'constants/mixpanelHelper';

// import { createPostApi } from 'api/post';
import { shareExperience } from 'services/engagement';
import { getApiBodyFromEditorStateAndMedia } from 'services/helpers/post';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import TextToggle from 'uiToolkit/TextToggle';

import { PanelFooter } from './CommonComps';

const SHARE_WITH_RECIPIENTS = [
  { value: 'CEO', label: 'CEO' },
  { value: 'HR Head', label: 'HR Head' },
  { value: 'Admin Team', label: 'Admin Team' },
];

const Drawer = styled(StyledDrawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      padding: 0;
    }
  }
`;

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderWrapper = styled.div`
  padding: 1.5rem 1.5rem;
  display: flex;
  justify-content: space-between;
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

const SubmissionWrapper = styled.div`
  padding: 0.5rem 0.8rem;
  display: flex;
  align-items: center;
`;

const Header = ({ closeClickHandler }) => (
  <HeaderWrapper>
    <div style={{ display: 'flex', 'align-items': 'center' }}>
      <StyledTypography customColor="textPrimary" variant="h6">
        Share experience
      </StyledTypography>
    </div>
    <NewStyledIconButton onClick={closeClickHandler} circular>
      <StyledIcon color="textSecondary" size="header">
        <CloseRoundedIcon />
      </StyledIcon>
    </NewStyledIconButton>
  </HeaderWrapper>
);

const editorPlaceholderStyles = css`
  font-size: ${(props) => props.theme.typography.body2.fontSize};
`;

const editorStyles = css`
  font-size: ${(props) => props.theme.typography.body2.fontSize};
  border-radius: 5px;
  min-height: 7rem;
  max-height: 15rem;
`;

export function createPost({ editorState, media, visibilityType, useMoney = false }) {
  const body = getApiBodyFromEditorStateAndMedia(editorState, media, useMoney);
  body.visibilityType = visibilityType;
  return body;
}

function PostEditField({ editorState, updateEditorState }) {
  return (
    <PostField
      placeholder="Type your experience here*"
      editorState={editorState}
      updateEditorState={updateEditorState}
      editorStyles={editorStyles}
      useMentionFunctionality
      useHashtagFunctionality={false}
      useHashtagDriverFunctionality
      placeholderStyles={editorPlaceholderStyles}
      widgetType={SEARCH_WIDGET_TYPES.post}
      allowNewLine
      teamId={null} // this is the right fix, you will be able to create hashtag like this, not like in the above case
    />
  );
}

const CreateExperiencePanel = ({ open, onCancel }) => {
  const [acceptSubmission, setAcceptSubmission] = useState(false);
  const [mentionUsed, setMentionUsed] = useState(false);
  const [state, setState] = useState({
    editorState: EditorState.createEmpty(),
    isAnonymous: true,
    sharedWith: SHARE_WITH_RECIPIENTS[2],
  });
  const dispatch = useDispatch();

  const toggleVisibility = (visibility) => {
    setState((currentState) => ({ ...currentState, isAnonymous: !visibility }));
  };

  const updateEditorState = (newEditorState) => {
    setState((currentState) => ({ ...currentState, editorState: newEditorState }));
    const postBody = getApiBodyFromEditorStateAndMedia(newEditorState);
    if (Object.keys(postBody.macros.employees).length > 0) {
      setMentionUsed(true);
    }
  };

  const updateSharedWith = (selectedOption) => {
    setState((currentState) => ({ ...currentState, sharedWith: selectedOption }));
  };

  const canSave = acceptSubmission && state.editorState.getCurrentContent().hasText();

  const saveExperience = () => {
    const postBody = getApiBodyFromEditorStateAndMedia(state.editorState);

    sendShareExperiencePublishedEvent({
      eventProperties: {
        shareGroup: state.sharedWith.value,
        anonymous: state.isAnonymous,
        feedbackText: postBody.text,
      },
    });

    shareExperience({
      sharedWith: state.sharedWith.value,
      isAnonymous: state.isAnonymous,
      experiencePost: {
        text: postBody.text,
        macros: postBody.macros,
        media: [],
      },
    })
      .then((res) => {
        dispatch(showSuccessSnackbar('Experience shared successfully'));
        onCancel();
      })
      .catch((err) => dispatch(showErrorSnackbar('An error occured while saving the experience')));
  };

  const clearForm = () => {
    setState({
      isAnonymous: true,
      sharedWith: SHARE_WITH_RECIPIENTS[2],
      editorState: EditorState.createEmpty(),
    });
    setAcceptSubmission(false);
  };

  useEffect(() => {
    clearForm();
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        onCancel();
      }}
      ModalProps={{
        onBackdropClick: () => {
          onCancel();
        },
      }}
    >
      <Wrapper>
        <Header
          closeClickHandler={() => {
            onCancel();
          }}
        />
        <StyledDivider variant="fullWidth" />

        <ContentWrapper>
          <StyledTypography variant="caption" customColor="textLabel" display="block" bottomGutter={0.8}>
            Share with*
          </StyledTypography>
          <Select
            placeholder="Select"
            options={SHARE_WITH_RECIPIENTS}
            onChange={updateSharedWith}
            value={state.sharedWith}
          />
          <TextToggle
            enabled={!state.isAnonymous}
            toggleHandler={toggleVisibility}
            enabledText="Signed"
            disabledText="Anonymous"
            topGutter={2}
          />
          <StyledTypography variant="caption" customColor="textLabel" display="block" bottomGutter={0.8} topGutter={2}>
            What would you like to share
          </StyledTypography>
          <PostEditField editorState={state.editorState} updateEditorState={updateEditorState} />
          {mentionUsed && (
            <StyledTypography
              variant="caption"
              customColor="textLabel"
              display="block"
              bottomGutter={0.5}
              topGutter={0.8}
            >
              Mentioning another teammate here won't notify them.
            </StyledTypography>
          )}
          <StyledTypography
            variant="caption"
            customColor="textLabel"
            display="block"
            bottomGutter={0.8}
            topGutter={0.5}
          >
            Please feel free to use hashtags(#) to add engagement drivers
          </StyledTypography>
        </ContentWrapper>

        <SubmissionWrapper>
          <Checkbox
            id="accept_submission_checkbox"
            checked={acceptSubmission}
            color="primary"
            onChange={() => setAcceptSubmission((val) => !val)}
          />
          <label style={{ cursor: 'pointer' }} htmlFor="accept_submission_checkbox">
            <StyledTypography customColor="textHeading" variant="body2">
              I am sure I want to submit this experience
            </StyledTypography>
          </label>
        </SubmissionWrapper>

        <StyledDivider variant="fullWidth" />
        <PanelFooter
          primaryText="Publish"
          secondaryText="Clear Form"
          primaryHandler={saveExperience}
          secondaryHandler={clearForm}
          canSave={canSave}
        />
      </Wrapper>
    </Drawer>
  );
};

export default CreateExperiencePanel;
